import { Copy, Edit } from 'lucide-react';
import { DynamicIcon } from 'lucide-react/dynamic';
import type { DateTime } from 'luxon';
import { useState } from 'react';
import { Alert, Button, Card, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type {
  Card as CardType,
  RecurrenceTaskStatus,
  User,
} from '../../service/api';
import api from '../../service/api';
import { type ManagerTaskItem } from '../../store/managerTasks';
import CardActionsList from '../CardActionsList';
import MultiUserTaskStatusModal from '../MultiUserTaskStatusModal';
import UserAvatar from '../UserAvatar';

interface UserTaskInfo {
  user: User;
  taskId: string;
  status: RecurrenceTaskStatus;
}

interface ManagerTaskCardProps {
  task: ManagerTaskItem;
  userTasks: UserTaskInfo[];
}

export default function ManagerTaskCard({
  task,
  userTasks,
}: ManagerTaskCardProps) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [isCopying, setIsCopying] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsCopying(true);
    setError(null);

    try {
      // Fetch the full recurrence data to copy
      const recurrence = await api.taskGetById(task.recurrenceId);

      // Navigate to create task page with copied data
      navigate('/manager/create-task', {
        state: {
          copyFrom: recurrence,
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to copy task');
    } finally {
      setIsCopying(false);
    }
  };

  const formatTime = (dateTime: DateTime) => {
    return dateTime.toLocal().toFormat('h:mm a');
  };

  const handleCardClick = () => {
    setShowModal(true);
  };

  const handleTitleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/manager/task/${task.recurrenceId}`);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  return (
    <>
      <Card
        className="mb-3 shadow-sm border"
        style={{ cursor: 'pointer' }}
        onClick={handleCardClick}
      >
        <Card.Body>
          <Row className="align-items-center">
            <Col xs="auto">
              <DynamicIcon name={task.icon} size={28} className="text-dark" />
            </Col>
            <Col>
              <h6 className="mb-0">{task.title}</h6>
            </Col>
            {typeof task.rewardCard !== 'string' &&
              (task.rewardCard as CardType).actions?.length > 0 && (
                <Col xs="auto">
                  <CardActionsList
                    actions={(task.rewardCard as CardType).actions}
                    size="xs"
                  />
                </Col>
              )}
            <Col xs="auto" className="d-flex gap-1">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleTitleClick}
                title={t('common.edit')}
              >
                <Edit size={16} />
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleCopy}
                disabled={isCopying}
                title={t('common.createCopy')}
              >
                {isCopying ? (
                  <div
                    className="spinner-border spinner-border-sm"
                    role="status"
                  />
                ) : (
                  <Copy size={16} />
                )}
              </Button>
            </Col>
          </Row>
          <Row className="align-items-center mt-2">
            <Col>
              <p className="text-muted small mb-1">
                {t('manager.tasks.scheduled')}: {formatTime(task.start)} -{' '}
                {formatTime(task.stop)}
              </p>
              <div className="d-flex align-items-center gap-1">
                <span className="text-muted small">
                  {t('manager.tasks.assignedUsers')}:
                </span>
                {userTasks.slice(0, 3).map(({ user }, index) => (
                  <UserAvatar
                    key={user._id}
                    user={user}
                    size={20}
                    className="me-1"
                    style={{ marginLeft: index > 0 ? '-8px' : '0' }}
                  />
                ))}
                {userTasks.length > 3 && (
                  <span className="text-muted small">
                    +{userTasks.length - 3}
                  </span>
                )}
              </div>
            </Col>
          </Row>
          {error && (
            <Alert variant="danger" className="mx-3 my-3 py-2">
              {error}
            </Alert>
          )}
        </Card.Body>
      </Card>

      <MultiUserTaskStatusModal
        isOpen={showModal}
        onClose={handleCloseModal}
        taskTitle={task.title}
        taskIcon={task.icon}
        start={task.start}
        stop={task.stop}
        recurrenceId={task.recurrenceId}
        userTasks={userTasks.map(ut => ({
          user: ut.user,
          taskId: ut.taskId,
          status: ut.status,
        }))}
      />
    </>
  );
}
